import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const HINT_ID = 'form-navigation-hint';
const LINK =
  'rounded-sm font-medium text-primary underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary';

/**
 * What still blocks the step (see proceedHint in missingFields.js). Field names are buttons that
 * take the vendor to the field; a verification sentence takes them to its panel.
 */
const ProceedHint = ({ hint, onFieldClick }) => {
  if (hint.kind === 'gate') {
    return onFieldClick ? (
      <button type="button" className={LINK} onClick={() => onFieldClick(hint.key)}>
        {hint.text}
      </button>
    ) : (
      <span className="font-medium">{hint.text}</span>
    );
  }
  return (
    <>
      <span className="font-medium text-foreground">Still needed:</span>{' '}
      {hint.names.map((name, i) => (
        <React.Fragment key={hint.keys[i]}>
          {i > 0 && ', '}
          {onFieldClick ? (
            <button type="button" className={LINK} onClick={() => onFieldClick(hint.keys[i])}>
              {name}
            </button>
          ) : (
            <span className="font-medium">{name}</span>
          )}
        </React.Fragment>
      ))}
      {hint.more > 0 && <span className="whitespace-nowrap"> +{hint.more} more</span>}
    </>
  );
};

const FormNavigation = ({
  currentStep,
  totalSteps,
  onPrevious,
  onNext,
  onSubmit,
  isSubmitting,
  // True while Next is saving the step. The only time Next is disabled — a click with fields
  // missing is how the vendor finds out which ones.
  isSaving = false,
  // proceedHint() from missingFields.js; null when nothing blocks the step.
  hint = null,
  onHintFieldClick,
  // Set while the vendor is editing a section they reached from the Review step, so the
  // primary action takes them straight back there instead of walking the rest of the form.
  nextLabel = 'Next',
  nextIcon = 'ChevronRight',
  submitLabel = 'Submit Registration',
}) => {
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === totalSteps;
  const describedBy = hint ? HINT_ID : undefined;

  return (
    <div className="bg-white/95 backdrop-blur-sm border-t border-border px-4 py-3 sm:px-6 sm:py-4 sticky bottom-0 z-50 shadow-lg">
      <div className="max-w-4xl mx-auto space-y-2 sm:space-y-3">
        {hint && (
          <div className="flex sm:justify-end">
            <div
              id={HINT_ID}
              role="status"
              className="flex max-w-full items-start gap-2 rounded-md border border-border bg-muted/70 px-3 py-2 text-sm leading-relaxed text-text-secondary"
            >
              <Icon name="Info" size={16} className="mt-0.5 flex-shrink-0 text-primary" />
              <p className="min-w-0 break-words">
                <ProceedHint hint={hint} onFieldClick={onHintFieldClick} />
              </p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          {/* Previous Button */}
          <div className="flex-1">
            {!isFirstStep && (
              <Button
                variant="outline"
                onClick={onPrevious}
                iconName="ChevronLeft"
                iconPosition="left"
                disabled={isSubmitting || isSaving}
              >
                Previous
              </Button>
            )}
          </div>

          {/* Step Indicator */}
          <div className="flex-1 text-center">
            <span className="text-sm text-text-secondary">
              Step {currentStep} of {totalSteps}
            </span>
          </div>

          {/* Next/Submit Button */}
          <div className="flex-1 flex justify-end">
            {isLastStep ? (
              <Button
                variant="default"
                onClick={onSubmit}
                loading={isSubmitting}
                disabled={isSubmitting}
                iconName="Send"
                iconPosition="right"
                aria-describedby={describedBy}
              >
                {isSubmitting ? 'Submitting...' : submitLabel}
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={onNext}
                loading={isSaving}
                disabled={isSaving}
                iconName={nextIcon}
                iconPosition="right"
                aria-describedby={describedBy}
              >
                {nextLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormNavigation;
